import type { TicketScope } from './serviceCalls/types';
import React, { useState, useEffect } from 'react';
import { useSiteWriteAccess } from '../hooks/useSiteWriteAccess';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useRBAC } from '../contexts/RBACContext';
import { POI, POICategory } from '../types';
import { apiService } from '../services/ApiService';
import {
  isPOIIfcLoaded as isPOIIfcLoadedUtil,
  getPOISourceFile,
  isPOIIfcLoading as isPOIIfcLoadingUtil
} from '../utils/POIFileUtils';
import { convertApiPoiToComponent } from '../utils/POIConverter';
import { getCategoryInfo as getIFCCategoryInfo, getCategoryColor, getIconForCategory } from '../constants/Categories';
import { parseCustomData as parsePOICustomData, deriveSubcategoryFromObjectType } from '../utils/POIFieldExtractor';
import SystemTreePanel from './systems/SystemTreePanel';
import { useToggleSet } from '../hooks/useToggleSet';
import { showErrorToast, showSuccessToast } from './shared/toast';
import { logger } from '../utils/log';

interface SystemsPageProps {
  focusedSystemId?: number | null;
  onSystemFocusHandled?: () => void;
  onOpenTickets?: (scope: TicketScope) => void;
  onCreateTicket?: (scope: TicketScope) => void;
  assetId?: string;
  onNavigateToViewer?: (poi: POI) => void;
  onHighlightGroup?: (pois: POI[], color?: string) => void; // Callback to highlight a group of POIs in 3D viewer
  loadedIfcFiles?: string[]; // Array of currently loaded IFC/RVT filenames in viewer
  loadingIfcFiles?: string[]; // Array of IFC/RVT filenames currently being loaded
  onRequestLoadModel?: (fileName: string) => void; // Callback to load an IFC model by filename
  onOpenDiagrams?: () => void;
  /** Notify that these POIs now carry a system assignment (thumbnail generation hook). */
  onSystemsAssigned?: (poiIds: string[]) => void; // Navigate to the relationship-diagram canvas (DT Alpha, Epic A)
}

interface SystemGroup {
  name: string;
  pois: POI[];
  count: number;
  icon: string;
}

interface SubCategoryGroup {
  name: string;
  pois: POI[];
  systems: SystemGroup[];
  count: number;
  icon: string;
}

interface CategoryGroup {
  category: string; // Now using original IFC category name
  pois: POI[];
  subCategories: SubCategoryGroup[];
  count: number;
  icon: string;
  description: string;
  color: string;
}

// Category definitions and helpers now live in src/constants/Categories.ts (DUP-12)

const SystemsPage: React.FC<SystemsPageProps> = ({ focusedSystemId, onSystemFocusHandled, onOpenTickets, onCreateTicket, assetId, onNavigateToViewer, onHighlightGroup, loadedIfcFiles, loadingIfcFiles, onRequestLoadModel, onOpenDiagrams, onSystemsAssigned }) => {
  const { colors } = useTheme();
  const { t, isRTL } = useTranslation();
  const { isAdmin, hasRole } = useRBAC();

  // RBAC-based permission check for edit operations
  // Only Data Creator can edit content; Site Admin manages permissions only
  // ONE write rule, the server's own (permission_level). This asked
  // `isAdmin || hasRole('data_creator', …)`, which hid this work from site admins although
  // every route behind it admits them — and, since migration 069, from an onboarding
  // assignee who is supposed to be filling the site in.
  const canEditContent = useSiteWriteAccess(assetId);
  const [pois, setPois] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const { set: expandedCategories, toggle: toggleCategoryExpanded } = useToggleSet<string>();
  const { set: expandedSubCategories, toggle: toggleSubCategoryExpanded } = useToggleSet<string>();
  const { set: expandedSystems, toggle: toggleSystemExpanded } = useToggleSet<string>();
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { set: expandedPOIs, toggle: togglePOIExpand } = useToggleSet<string>();

  // Set Systems modal state
  const [showSetSystemsModal, setShowSetSystemsModal] = useState(false);
  const [availableSystemProperties, setAvailableSystemProperties] = useState<string[]>([]);
  const [propertyValues, setPropertyValues] = useState<Record<string, string[]>>({});
  // New dynamic hierarchy: System (top) + any number of subcategory levels
  const [selectedSystemProperty, setSelectedSystemProperty] = useState<string>(''); // Top level - System
  const [subcategoryLevels, setSubcategoryLevels] = useState<string[]>(['']); // Array of subcategory property names (starts with 1)
  const [isLoadingSystemProperties, setIsLoadingSystemProperties] = useState(false);
  const [isReassigningSystems, setIsReassigningSystems] = useState(false);
  const [reassignSystemsResult, setReassignSystemsResult] = useState<{ updated: number; unchanged: number; failed: number; total: number } | null>(null);

  // Property search filters for Set Systems modal (dynamic)
  const [systemPropertySearch, setSystemPropertySearch] = useState(''); // For top-level System
  const [subcategorySearches, setSubcategorySearches] = useState<string[]>(['']); // For each subcategory level

  // Resolved asset ID for API calls
  const [resolvedAssetId, setResolvedAssetId] = useState<string | null>(null);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to check if a POI's source file (IFC or RVT) is currently loaded in viewer
  // fallbackSourceFile: optional source file to use if POI doesn't have one (e.g., from parent context)
  const isPOIIfcLoaded = (poi: POI | any, fallbackSourceFile?: string): boolean =>
    isPOIIfcLoadedUtil(poi, loadedIfcFiles, fallbackSourceFile);

  // Helper function to check if a POI's IFC file is currently loading
  // fallbackSourceFile: optional source file to use if POI doesn't have one (e.g., from parent context)
  const isPOIIfcLoading = (poi: POI | any, fallbackSourceFile?: string): boolean =>
    isPOIIfcLoadingUtil(poi, loadingIfcFiles, fallbackSourceFile);

  // Helper function to check if any POI in a group has its source file loaded
  // Used to determine if 3D group button should be enabled
  const hasAnyLoadedPOI = (pois: POI[]): boolean => {
    if (!loadedIfcFiles || loadedIfcFiles.length === 0) return false;
    return pois.some(poi => isPOIIfcLoaded(poi));
  };

  // Helper function to get unique unloaded model files from a group of POIs
  // Returns array of unique source file names that are not loaded and not loading
  const getUnloadedModelsFromGroup = (pois: POI[]): string[] => {
    const unloadedModels = new Set<string>();
    for (const poi of pois) {
      const sourceFile = getPOISourceFile(poi);
      if (sourceFile && !isPOIIfcLoaded(poi) && !isPOIIfcLoading(poi)) {
        unloadedModels.add(sourceFile);
      }
    }
    return Array.from(unloadedModels);
  };

  // Helper function to check if any model in a group is currently loading
  const hasAnyLoadingModel = (pois: POI[]): boolean => {
    return pois.some(poi => isPOIIfcLoading(poi));
  };

  // Category info/color helpers are imported from '../constants/Categories'
  // (getIFCCategoryInfo is the shared getCategoryInfo; getCategoryColor is shared)

  // Keep mapIFCCategoryToPOICategory for backwards compatibility (used in POI conversion)
  const mapIFCCategoryToPOICategory = (ifcCategory: string): POICategory => {
    switch (ifcCategory?.toLowerCase()) {
      case 'structure':
      case 'architecture':
        return POICategory.STRUCTURE;
      case 'spatial':
        return POICategory.ROOM;
      case 'hvac':
      case 'electrical':
      case 'plumbing':
      case 'fire protection':
      case 'equipment':
        return POICategory.SYSTEM;
      case 'furniture':
        return POICategory.UTILITY;
      default:
        return POICategory.UTILITY;
    }
  };

  // Helper function to extract sub-category from POI data
  const extractSubCategory = (apiPoi: any): string => {
    // Canonical ObjectType derivation (utils/POIFieldExtractor, E-12c)
    return deriveSubcategoryFromObjectType(apiPoi) || apiPoi.poi_type_name || 'Other';
  };

  // Sub-category icon — the canonical keyword table (Categories.getIconForCategory).
  // The page-local map that lived here disagreed with the canonical on shared concepts
  // (🔧/🌬️/🧱/▫️ vs ⚙️/🌡️/🏗️/📐) — canonical wins so the same POI reads the same
  // on every page (2026-07-06).
  const getSubCategoryIcon = (subCategoryName: string): string => getIconForCategory(subCategoryName);

  // Load POIs from API
  const loadPOIs = async () => {
    let currentAssetId = assetId;
    if (!currentAssetId) {
      // Try to get the selected asset from local storage
      const storedAssetId = localStorage.getItem('selectedAssetId');
      if (!storedAssetId) {
        setError(t('noAssetSelectedMessage'));
        setLoading(false);
        return;
      }
      currentAssetId = storedAssetId;
    }

    // Set resolved asset ID for other functions to use
    setResolvedAssetId(currentAssetId);

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get(`/assets/${currentAssetId}/pois`);
      const poisArray = Array.isArray(data) ? data : (data.pois || []);

      // DEBUG: Log first API POI to verify data structure
      if (poisArray.length > 0) {
        logger.debug('🟡 SystemsPage - First API POI:', poisArray[0]);
        logger.debug('🟡 SystemsPage - ifc_element_express_id:', poisArray[0].ifc_element_express_id);
        logger.debug('🟡 SystemsPage - ifc_element_guid:', poisArray[0].ifc_element_guid);
      }

      // Convert API data to POI format expected by component (shared util)
      const convertedPOIs = poisArray.map((apiPoi: any) => convertApiPoiToComponent(apiPoi, {
        mapCategory: (poi) => mapIFCCategoryToPOICategory(poi.ifc_category),
        extractSubCategory,
        getColor: (poi) => getCategoryColor(poi.ifc_category || 'Other'),
        extraProperties: {
          // User-assigned system hierarchy (from Set Systems feature)
          // New dynamic structure: systemCategory = System (top level), subcategoryLevels = JSON array
          systemCategory: apiPoi.system_category || undefined,
          // Parse subcategoryLevels from JSON array (stored in system_subcategory)
          subcategoryLevels: (() => {
            try {
              if (apiPoi.system_subcategory) {
                const parsed = JSON.parse(apiPoi.system_subcategory);
                return Array.isArray(parsed) ? parsed : [apiPoi.system_subcategory];
              }
              return [];
            } catch {
              // Fallback: treat as single value (backward compatibility)
              return apiPoi.system_subcategory ? [apiPoi.system_subcategory] : [];
            }
          })(),
          // Keep for backward compatibility
          systemSubcategory: apiPoi.system_subcategory || undefined,
          systemName: apiPoi.system_name || undefined
        }
      }));

      // DEBUG: Log first converted POI to verify conversion
      if (convertedPOIs.length > 0) {
        logger.debug('🟢 SystemsPage - First converted POI:', convertedPOIs[0]);
        logger.debug('🟢 SystemsPage - converted ifcElementExpressId:', convertedPOIs[0].properties?.ifcElementExpressId);
        logger.debug('🟢 SystemsPage - converted ifcElementGuid:', convertedPOIs[0].properties?.ifcElementGuid);
      }

      setPois(convertedPOIs);

      // Categories start collapsed by default
      // Users can expand individual categories as needed
      return convertedPOIs;
    } catch (err: any) {
      console.error('Failed to load POIs:', err);
      setError(err.message || 'Failed to load POIs');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPOIs();
    // Load once per asset. loadPOIs also closes over `t`, so depending on it would
    // re-fetch every POI on a language switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  // Category/subcategory/system expand toggles are provided by useToggleSet
  // (toggleCategoryExpanded, toggleSubCategoryExpanded, toggleSystemExpanded)

  const getCategoryInfo = (category: POICategory): { icon: string; description: string; color: string } => {
    switch (category) {
      case POICategory.STRUCTURE:
        return { icon: '🏗️', description: 'Structural elements and access points', color: '#E94B3C' };
      case POICategory.ROOM:
        return { icon: '🚪', description: 'Rooms and spatial elements', color: '#88B04B' };
      case POICategory.SYSTEM:
        return { icon: '🔧', description: 'Mechanical, Electrical, and Plumbing systems', color: '#FF6B6B' };
      case POICategory.SAFETY:
        return { icon: '🚨', description: 'Safety and emergency equipment', color: '#FF4757' };
      case POICategory.UTILITY:
        return { icon: '🔌', description: 'Utilities and infrastructure', color: '#6B5B95' };
      default:
        return { icon: '📍', description: 'General points of interest', color: '#808080' };
    }
  };

  const getTypeIcon = (poi: POI): string => {
    // Return type-specific icons based on POI properties
    const type = poi.properties.type?.toLowerCase() || '';

    if (type.includes('wall')) return '🧱';
    if (type.includes('door')) return '🚪';
    if (type.includes('window')) return '🪟';
    if (type.includes('beam')) return '🏗️';
    if (type.includes('column')) return '🏛️';
    if (type.includes('slab')) return '🧱';
    if (type.includes('stair')) return '🪜';
    if (type.includes('roof')) return '🏠';
    if (type.includes('furniture')) return '🪑';
    if (type.includes('space')) return '📐';
    if (type.includes('building')) return '🏢';

    return getCategoryInfo(poi.category).icon;
  };

  // POI expand/collapse toggle is provided by useToggleSet (togglePOIExpand)

  // Helper function to parse custom_data JSON for POI details
  const parseCustomData = (customData: any) => {
    try {
      if (typeof customData === 'string') {
        return JSON.parse(customData);
      }
      return customData || {};
    } catch {
      return {};
    }
  };

  // Helper function to format property values for display
  const formatPropertyValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toFixed(3);
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Set Systems Functions
  const handleOpenSetSystemsModal = async () => {
    setShowSetSystemsModal(true);
    setReassignSystemsResult(null);
    setSelectedSystemProperty(''); // Top-level System
    setSubcategoryLevels(['']); // Start with one empty subcategory level
    setSystemPropertySearch('');
    setSubcategorySearches(['']); // Start with one empty search
    await loadAvailableSystemProperties();
  };

  // Add a new subcategory level
  const addSubcategoryLevel = () => {
    setSubcategoryLevels([...subcategoryLevels, '']);
    setSubcategorySearches([...subcategorySearches, '']);
  };

  // Remove a subcategory level
  const removeSubcategoryLevel = (index: number) => {
    if (subcategoryLevels.length <= 1) return; // Keep at least one
    const newLevels = subcategoryLevels.filter((_, i) => i !== index);
    const newSearches = subcategorySearches.filter((_, i) => i !== index);
    setSubcategoryLevels(newLevels);
    setSubcategorySearches(newSearches);
  };

  // Update a specific subcategory level
  const updateSubcategoryLevel = (index: number, value: string) => {
    const newLevels = [...subcategoryLevels];
    newLevels[index] = value;
    setSubcategoryLevels(newLevels);
  };

  // Update a specific subcategory search
  const updateSubcategorySearch = (index: number, value: string) => {
    const newSearches = [...subcategorySearches];
    newSearches[index] = value;
    setSubcategorySearches(newSearches);
  };

  const loadAvailableSystemProperties = async () => {
    if (!resolvedAssetId) return;

    try {
      setIsLoadingSystemProperties(true);
      const data = await apiService.get(`/assets/${resolvedAssetId}/pois/available-system-properties`);
      setAvailableSystemProperties(data.properties || []);
      setPropertyValues(data.propertyValues || {});
    } catch (error) {
      console.error('Failed to load available system properties:', error);
      setAvailableSystemProperties([]);
      setPropertyValues({});
    } finally {
      setIsLoadingSystemProperties(false);
    }
  };

  const handleReassignSystems = async () => {
    // Need at least the system property selected
    if (!resolvedAssetId || !selectedSystemProperty) {
      showErrorToast('Please select a System property (top level)');
      return;
    }

    try {
      setIsReassigningSystems(true);
      setReassignSystemsResult(null);

      // Filter out empty subcategory levels
      const validSubcategories = subcategoryLevels.filter(level => level !== '');

      const result = await apiService.post(`/assets/${resolvedAssetId}/pois/reassign-systems`, {
        systemProperty: selectedSystemProperty, // Top level - required
        subcategoryLevels: validSubcategories // Array of subcategory properties
      });
      setReassignSystemsResult(result);

      // Reload POIs to reflect changes
      const freshPois = await loadPOIs();

      // Kick element-picture generation for the now-curated equipment
      // (the generator itself skips already-photographed POIs).
      if (onSystemsAssigned && Array.isArray(freshPois)) {
        const assigned = freshPois
          .filter(poi => (poi.properties as any)?.systemCategory)
          // dbId = the SERIAL PK; poi.id prefers the EXTERNAL poi_id string,
          // which server routes (and the generator's /pois lookup) don't know.
          .map(poi => String((poi as any).dbId ?? poi.id));
        if (assigned.length > 0) {
          onSystemsAssigned(assigned);
          showSuccessToast(`Generating element pictures for ${assigned.length} assets in the background…`);
        }
      }

    } catch (error: any) {
      console.error('Failed to reassign systems:', error);
      showErrorToast(`Failed to reassign systems: ${error.message}`);
    } finally {
      setIsReassigningSystems(false);
    }
  };

  // Group POIs by System (top level) and dynamic subcategory levels
  // New structure: systemCategory (System) + subcategoryLevels array
  // Falls back to original IFC category and subCategory when not assigned
  const groupPOIsByCategory = (): CategoryGroup[] => {
    const groups: Record<string, CategoryGroup> = {};

    // Group by System (top level) - prioritize user-assigned systemCategory
    const categoryGroups: Record<string, POI[]> = {};
    pois.forEach(poi => {
      // Use user-assigned systemCategory (System) if available, otherwise fall back to IFC category
      const category = (poi.properties as any).systemCategory || poi.properties.originalCategory || 'Other';

      if (!categoryGroups[category]) {
        categoryGroups[category] = [];
      }
      categoryGroups[category].push(poi);
    });

    // Create category groups with dynamic subcategory levels
    Object.entries(categoryGroups).forEach(([category, categoryPois]) => {
      const info = getIFCCategoryInfo(category);

      // Get subcategory level from POI (first level in subcategoryLevels array)
      const getSubcategoryLevel = (poi: POI, levelIndex: number): string => {
        const props = poi.properties as any;
        const levels = props.subcategoryLevels || [];
        if (levels[levelIndex]) return levels[levelIndex];
        // Fallback for backward compatibility
        if (levelIndex === 0) return props.subCategory || 'Other';
        return 'Unassigned';
      };

      // Group POIs by first subcategory level
      const subCategoryMap: Record<string, POI[]> = {};
      categoryPois.forEach(poi => {
        const subCat = getSubcategoryLevel(poi, 0);
        if (!subCategoryMap[subCat]) {
          subCategoryMap[subCat] = [];
        }
        subCategoryMap[subCat].push(poi);
      });

      // Create sub-category groups with systems (second subcategory level)
      const subCategories: SubCategoryGroup[] = Object.entries(subCategoryMap).map(([name, subPois]) => {
        // Group POIs by second subcategory level
        const systemMap: Record<string, POI[]> = {};
        subPois.forEach(poi => {
          const systemName = getSubcategoryLevel(poi, 1);
          if (!systemMap[systemName]) {
            systemMap[systemName] = [];
          }
          systemMap[systemName].push(poi);
        });

        // Create system groups (third level - can show additional levels in POI details)
        const systems: SystemGroup[] = Object.entries(systemMap).map(([sysName, sysPois]) => ({
          name: sysName,
          pois: sysPois,
          count: sysPois.length,
          icon: getSubCategoryIcon(sysName)
        }));

        return {
          name,
          pois: subPois,
          systems,
          count: subPois.length,
          icon: getSubCategoryIcon(name)
        };
      });

      groups[category] = {
        category: category,
        pois: categoryPois,
        subCategories,
        count: categoryPois.length,
        icon: info.icon,
        description: info.description,
        color: info.color
      };
    });

    // Helper function to search property set values
    const searchPropertySetValues = (poi: POI, searchLower: string): boolean => {
      try {
        // POI interface stores custom_data in properties.metadata (already parsed)
        const customData = poi.properties?.metadata;

        if (!customData?.ifc?.propertySets) return false;

        // Search through all property set values
        for (const [, properties] of Object.entries(customData.ifc.propertySets)) {
          if (properties && typeof properties === 'object') {
            for (const [, value] of Object.entries(properties as Record<string, any>)) {
              if (value !== null && value !== undefined) {
                const strValue = String(value).toLowerCase();
                if (strValue.includes(searchLower)) {
                  return true;
                }
              }
            }
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
      return false;
    };

    // Filter out empty categories and apply search
    const searchLower = searchTerm.toLowerCase();
    return Object.values(groups)
      .filter(group => group.count > 0)
      .map(group => {
        // Filter POIs based on search (including property set values)
        const filteredPois = group.pois.filter(poi =>
          !searchTerm.trim() ||
          poi.name.toLowerCase().includes(searchLower) ||
          poi.properties.description?.toLowerCase().includes(searchLower) ||
          poi.properties.type.toLowerCase().includes(searchLower) ||
          poi.properties.system?.toLowerCase().includes(searchLower) ||
          poi.properties.location?.toLowerCase().includes(searchLower) ||
          poi.properties.subCategory?.toLowerCase().includes(searchLower) ||
          searchPropertySetValues(poi, searchLower)  // Search in property set values
        );

        // Update sub-categories and systems based on filtered POIs
        const filteredSubCategories = group.subCategories
          .map(subCat => {
            const filteredSubPois = subCat.pois.filter(poi => filteredPois.includes(poi));
            // Also filter systems within subcategory
            const filteredSystems = subCat.systems
              .map(sys => ({
                ...sys,
                pois: sys.pois.filter(poi => filteredPois.includes(poi)),
                count: sys.pois.filter(poi => filteredPois.includes(poi)).length
              }))
              .filter(sys => sys.count > 0);

            return {
              ...subCat,
              pois: filteredSubPois,
              systems: filteredSystems,
              count: filteredSubPois.length
            };
          })
          .filter(subCat => subCat.count > 0);

        return {
          ...group,
          pois: filteredPois,
          subCategories: filteredSubCategories,
          count: filteredPois.length
        };
      })
      .filter(group => group.pois.length > 0); // Remove categories with no matching POIs
  };

  const categoryGroups = groupPOIsByCategory();

  const renderPOI = (poi: POI) => {
    const isExpanded = expandedPOIs.has(poi.id);
    const customData = parseCustomData(poi.properties?.metadata);

    return (
      <div key={poi.id} style={{ marginInlineStart: '20px', marginBottom: '8px' }}>
        <div
          className={`location-card ${selectedPOI?.id === poi.id ? 'selected' : ''}`}
          onClick={() => togglePOIExpand(poi.id)}
          style={{ cursor: 'pointer' }}
        >
          <div className="location-info">
            <span style={{
              fontSize: '10px',
              color: colors.textSecondary,
              marginInlineEnd: '4px'
            }}>
              {isExpanded ? '\u25BC' : (isRTL ? '\u25C0' : '\u25B6')}
            </span>
            <span style={{ fontSize: '18px' }}>
              {getTypeIcon(poi)}
            </span>

            <div>
              <div style={{ fontWeight: 500, color: colors.textPrimary, fontSize: '15px' }}>
                {poi.name}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                {poi.properties.type}
                {poi.properties.system && ` \u2022 ${poi.properties.system}`}
              </div>
              <div style={{
                fontSize: '10px',
                color: colors.textSecondary,
                fontFamily: 'monospace',
                marginTop: '2px'
              }}>
                ({poi.coordinates.x.toFixed(1)}, {poi.coordinates.y.toFixed(1)}, {poi.coordinates.z?.toFixed(1) || '0'})
              </div>
              {poi.properties.location && (
                <div style={{ fontSize: '10px', color: colors.textTertiary || colors.textSecondary, marginTop: '1px' }}>
                  {poi.properties.location}
                </div>
              )}
              {poi.properties.description && (
                <div style={{ fontSize: '10px', color: colors.textSecondary, marginTop: '1px' }}>
                  {poi.properties.description}
                </div>
              )}
            </div>
          </div>

          <div className="location-actions">
            {onNavigateToViewer && isPOIIfcLoaded(poi) && (
              <button
                className="btn btn-primary btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToViewer(poi);
                }}
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2'
                }}
              >
                3D
              </button>
            )}
            {/* Loading indicator when model is being loaded */}
            {isPOIIfcLoading(poi) && (
              <span
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2',
                  color: colors.textSecondary,
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={`Loading ${getPOISourceFile(poi)}...`}
              >
                <span style={{
                  width: '10px',
                  height: '10px',
                  border: '2px solid ' + colors.border,
                  borderTopColor: colors.primary,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block'
                }} />
                Loading...
              </span>
            )}
            {/* Load Model button when model is not loaded and not loading */}
            {onRequestLoadModel && !isPOIIfcLoaded(poi) && !isPOIIfcLoading(poi) && getPOISourceFile(poi) && (
              <button
                className="btn btn-secondary btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  const sourceFile = getPOISourceFile(poi);
                  if (sourceFile) onRequestLoadModel(sourceFile);
                }}
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2'
                }}
                title={`Load ${getPOISourceFile(poi)} to 3D viewer`}
              >
                Load Model
              </button>
            )}
          </div>
        </div>

        {/* Expanded POI Details */}
        {isExpanded && (
          <div style={{
            marginInlineStart: '28px',
            marginTop: '8px',
            padding: '12px',
            background: colors.backgroundSecondary,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            fontSize: '12px'
          }}>
            {/* Classification Section */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '6px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>
                Classification
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                {poi.properties.system && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Group:</span>
                    <span style={{ color: colors.textPrimary }}>{poi.properties.system}</span>
                  </>
                )}
                {poi.properties.originalCategory && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Category:</span>
                    <span style={{ color: colors.textPrimary }}>{poi.properties.originalCategory}</span>
                  </>
                )}
                {poi.properties.type && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Type:</span>
                    <span style={{ color: colors.textPrimary }}>{poi.properties.type}</span>
                  </>
                )}
                {poi.properties.subCategory && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Subcategory:</span>
                    <span style={{ color: colors.textPrimary }}>{poi.properties.subCategory}</span>
                  </>
                )}
                {customData?.ifc?.importance && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Importance:</span>
                    <span style={{ color: colors.textPrimary }}>{customData.ifc.importance}</span>
                  </>
                )}
              </div>
            </div>

            {/* Location Context Section */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '6px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>
                Location Context
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                {poi.properties.location && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Source File:</span>
                    <span style={{ color: colors.textPrimary, wordBreak: 'break-all' }}>{poi.properties.location}</span>
                  </>
                )}
                {customData?.ifc?.coordinateSystem && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Coordinate System:</span>
                    <span style={{ color: colors.textPrimary }}>{customData.ifc.coordinateSystem}</span>
                  </>
                )}
                <span style={{ color: colors.textSecondary }}>Position:</span>
                <span style={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                  X: {poi.coordinates.x.toFixed(3)}, Y: {poi.coordinates.y.toFixed(3)}, Z: {(poi.coordinates.z || 0).toFixed(3)}
                </span>
                {customData?.ifc?.locationId && (
                  <>
                    <span style={{ color: colors.textSecondary }}>Location ID:</span>
                    <span style={{ color: colors.textPrimary }}>{customData.ifc.locationId}</span>
                  </>
                )}
              </div>
            </div>

            {/* IFC Element Details Section */}
            {(poi.properties.ifcElementExpressId || poi.properties.ifcElementGuid || customData?.ifc?.ifcType) && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '6px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>
                  IFC Element Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                  {poi.properties.ifcElementExpressId && (
                    <>
                      <span style={{ color: colors.textSecondary }}>Express ID:</span>
                      <span style={{ color: colors.textPrimary, fontFamily: 'monospace' }}>{poi.properties.ifcElementExpressId}</span>
                    </>
                  )}
                  {poi.properties.ifcElementGuid && (
                    <>
                      <span style={{ color: colors.textSecondary }}>GUID:</span>
                      <span style={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '10px', wordBreak: 'break-all' }}>{poi.properties.ifcElementGuid}</span>
                    </>
                  )}
                  {customData?.ifc?.ifcType && (
                    <>
                      <span style={{ color: colors.textSecondary }}>IFC Type:</span>
                      <span style={{ color: colors.textPrimary }}>{customData.ifc.ifcType}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Property Sets Section */}
            {customData?.ifc?.propertySets && Object.keys(customData.ifc.propertySets).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '6px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>
                  Property Sets
                </div>
                {Object.entries(customData.ifc.propertySets).map(([setName, properties]: [string, any]) => (
                  <div key={setName} style={{ marginBottom: '8px' }}>
                    <div style={{ fontWeight: 500, color: colors.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                      {setName}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', paddingLeft: '8px' }}>
                      {properties && typeof properties === 'object' && Object.entries(properties).map(([propName, propValue]: [string, any]) => (
                        <React.Fragment key={propName}>
                          <span style={{ color: colors.textSecondary, fontSize: '11px' }}>{propName}:</span>
                          <span style={{ color: colors.textPrimary, fontSize: '11px', wordBreak: 'break-word' }}>{formatPropertyValue(propValue)}</span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Extended Properties Section */}
            {customData?.ifc?.properties && Object.keys(customData.ifc.properties).length > 0 && (
              <div>
                <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: '6px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>
                  Extended Properties
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                  {Object.entries(customData.ifc.properties).map(([propName, propValue]: [string, any]) => (
                    <React.Fragment key={propName}>
                      <span style={{ color: colors.textSecondary }}>{propName}:</span>
                      <span style={{ color: colors.textPrimary, wordBreak: 'break-word' }}>{formatPropertyValue(propValue)}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSystem = (system: SystemGroup, subCategory: SubCategoryGroup, categoryGroup: CategoryGroup) => {
    const systemKey = `${categoryGroup.category}_${subCategory.name}_${system.name}`;
    const isExpanded = expandedSystems.has(systemKey);

    return (
      <div key={system.name} style={{ marginInlineStart: '20px', marginBottom: '8px' }}>
        <div
          className="location-card"
          onClick={() => toggleSystemExpanded(systemKey)}
          style={{
            background: `linear-gradient(135deg, ${categoryGroup.color}05, ${categoryGroup.color}02)`,
            borderColor: `${categoryGroup.color}30`,
            borderWidth: '1px',
            cursor: 'pointer'
          }}
        >
          <div className="location-info">
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: '10px',
                color: colors.textSecondary
              }}
            >
              {isExpanded ? '\u25BC' : '\u25B6'}
            </button>

            <span style={{ fontSize: '14px' }}>
              {system.icon}
            </span>

            <div>
              <div style={{
                fontWeight: 500,
                color: colors.textPrimary,
                fontSize: '13px'
              }}>
                {system.name}
              </div>
              <div style={{ fontSize: '10px', color: colors.textSecondary, marginTop: '1px' }}>
                System
              </div>
            </div>
          </div>

          <div className="location-actions">
            {/* 3D button - shown when model is loaded */}
            {onHighlightGroup && hasAnyLoadedPOI(system.pois) && (
              <button
                className="btn btn-primary btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  onHighlightGroup(system.pois, categoryGroup.color);
                }}
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2',
                  marginRight: '4px'
                }}
                title={`Show all ${system.count} elements in 3D`}
              >
                3D
              </button>
            )}
            {/* Loading indicator */}
            {hasAnyLoadingModel(system.pois) && (
              <span style={{
                fontSize: '10px',
                color: colors.textSecondary,
                marginRight: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span className="loading-spinner" style={{ width: '10px', height: '10px', display: 'inline-block' }} />
                Loading...
              </span>
            )}
            {/* Load Model button - shown when model is not loaded and not loading */}
            {(() => {
              const unloadedModels = getUnloadedModelsFromGroup(system.pois);
              if (onRequestLoadModel && unloadedModels.length > 0 && !hasAnyLoadingModel(system.pois)) {
                return (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Load the first unloaded model (typically there's only one per group)
                      onRequestLoadModel(unloadedModels[0]);
                    }}
                    style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      lineHeight: '1.2',
                      marginRight: '4px'
                    }}
                    title={`Load ${unloadedModels.length > 1 ? unloadedModels.length + ' models' : unloadedModels[0]}`}
                  >
                    Load Model
                  </button>
                );
              }
              return null;
            })()}
            <span style={{
              background: `${categoryGroup.color}15`,
              color: categoryGroup.color,
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: 500
            }}>
              {system.count} element{system.count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: '4px' }}>
            {system.pois.map(poi => renderPOI(poi))}
          </div>
        )}
      </div>
    );
  };

  const renderSubCategory = (subCategory: SubCategoryGroup, categoryGroup: CategoryGroup) => {
    const subCategoryKey = `${categoryGroup.category}_${subCategory.name}`;
    const isExpanded = expandedSubCategories.has(subCategoryKey);

    return (
      <div key={subCategory.name} style={{ marginInlineStart: '20px', marginBottom: '12px' }}>
        <div
          className="location-card"
          onClick={() => toggleSubCategoryExpanded(subCategoryKey)}
          style={{
            background: `linear-gradient(135deg, ${categoryGroup.color}08, ${categoryGroup.color}03)`,
            borderColor: `${categoryGroup.color}40`,
            borderWidth: '1px',
            cursor: 'pointer'
          }}
        >
          <div className="location-info">
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: '10px',
                color: colors.textSecondary
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </button>

            <span style={{ fontSize: '16px' }}>
              {subCategory.icon}
            </span>

            <div>
              <div style={{
                fontWeight: 500,
                color: colors.textPrimary,
                fontSize: '14px'
              }}>
                {subCategory.name}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '1px' }}>
                Sub-category
              </div>
            </div>
          </div>

          <div className="location-actions">
            {/* 3D button - shown when model is loaded */}
            {onHighlightGroup && hasAnyLoadedPOI(subCategory.pois) && (
              <button
                className="btn btn-primary btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  onHighlightGroup(subCategory.pois, categoryGroup.color);
                }}
                style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  lineHeight: '1.2',
                  marginRight: '4px'
                }}
                title={`Show all ${subCategory.count} elements in 3D`}
              >
                3D
              </button>
            )}
            {/* Loading indicator */}
            {hasAnyLoadingModel(subCategory.pois) && (
              <span style={{
                fontSize: '10px',
                color: colors.textSecondary,
                marginRight: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span className="loading-spinner" style={{ width: '10px', height: '10px', display: 'inline-block' }} />
                Loading...
              </span>
            )}
            {/* Load Model button - shown when model is not loaded and not loading */}
            {(() => {
              const unloadedModels = getUnloadedModelsFromGroup(subCategory.pois);
              if (onRequestLoadModel && unloadedModels.length > 0 && !hasAnyLoadingModel(subCategory.pois)) {
                return (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestLoadModel(unloadedModels[0]);
                    }}
                    style={{
                      fontSize: '10px',
                      padding: '2px 6px',
                      lineHeight: '1.2',
                      marginRight: '4px'
                    }}
                    title={`Load ${unloadedModels.length > 1 ? unloadedModels.length + ' models' : unloadedModels[0]}`}
                  >
                    Load Model
                  </button>
                );
              }
              return null;
            })()}
            <span style={{
              background: `${categoryGroup.color}20`,
              color: categoryGroup.color,
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 500
            }}>
              {subCategory.count} item{subCategory.count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: '6px' }}>
            {subCategory.systems.map(system => renderSystem(system, subCategory, categoryGroup))}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = (categoryGroup: CategoryGroup) => {
    const isExpanded = expandedCategories.has(categoryGroup.category);
    const filteredCount = categoryGroup.pois.length;

    return (
      <div key={categoryGroup.category} style={{ marginBottom: '16px' }}>
        <div
          className="location-card"
          onClick={() => toggleCategoryExpanded(categoryGroup.category)}
          style={{
            background: `linear-gradient(135deg, ${categoryGroup.color}15, ${categoryGroup.color}05)`,
            borderColor: categoryGroup.color,
            borderWidth: '2px',
            cursor: 'pointer'
          }}
        >
          <div className="location-info">
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: '12px',
                color: colors.textSecondary
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </button>

            <span style={{ fontSize: '24px' }}>
              {categoryGroup.icon}
            </span>

            <div>
              <div style={{
                fontWeight: 600,
                color: categoryGroup.color,
                fontSize: '18px'
              }}>
                {categoryGroup.category}
              </div>
              <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '2px' }}>
                {categoryGroup.description}
              </div>
            </div>
          </div>

          <div className="location-actions">
            {/* 3D button - shown when model is loaded */}
            {onHighlightGroup && hasAnyLoadedPOI(categoryGroup.pois) && (
              <button
                className="btn btn-primary btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  onHighlightGroup(categoryGroup.pois, categoryGroup.color);
                }}
                style={{
                  fontSize: '11px',
                  padding: '3px 8px',
                  lineHeight: '1.2',
                  marginRight: '6px'
                }}
                title={`Show all ${filteredCount} elements in 3D`}
              >
                3D
              </button>
            )}
            {/* Loading indicator */}
            {hasAnyLoadingModel(categoryGroup.pois) && (
              <span style={{
                fontSize: '11px',
                color: colors.textSecondary,
                marginRight: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span className="loading-spinner" style={{ width: '12px', height: '12px', display: 'inline-block' }} />
                Loading...
              </span>
            )}
            {/* Load Model button - shown when model is not loaded and not loading */}
            {(() => {
              const unloadedModels = getUnloadedModelsFromGroup(categoryGroup.pois);
              if (onRequestLoadModel && unloadedModels.length > 0 && !hasAnyLoadingModel(categoryGroup.pois)) {
                return (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestLoadModel(unloadedModels[0]);
                    }}
                    style={{
                      fontSize: '11px',
                      padding: '3px 8px',
                      lineHeight: '1.2',
                      marginRight: '6px'
                    }}
                    title={`Load ${unloadedModels.length > 1 ? unloadedModels.length + ' models' : unloadedModels[0]}`}
                  >
                    Load Model
                  </button>
                );
              }
              return null;
            })()}
            <span style={{
              background: categoryGroup.color,
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              {filteredCount} Asset{filteredCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {isExpanded && filteredCount > 0 && (
          <div style={{ marginTop: '8px' }}>
            {categoryGroup.subCategories.length > 0 ? (
              categoryGroup.subCategories
                .filter(subCat => subCat.pois.some(poi => categoryGroup.pois.includes(poi))) // Only show sub-categories with filtered POIs
                .map(subCategory => renderSubCategory(subCategory, categoryGroup))
            ) : (
              // Fallback: show POIs directly if no sub-categories
              categoryGroup.pois.map(poi => renderPOI(poi))
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: colors.textSecondary }}>{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '40px'
      }}>
        <div style={{
          backgroundColor: colors.backgroundSecondary,
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕹️</div>
          <h3 style={{ color: colors.textPrimary, marginBottom: '12px' }}>{t('systems')}</h3>
          <p style={{ color: colors.textSecondary, marginBottom: '24px' }}>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => loadPOIs()}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  const totalPOIs = pois.length;
  const filteredPOIs = categoryGroups.reduce((sum, group) => sum + group.pois.length, 0);

  return (
    <div className="locations-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="locations-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h1 style={{ margin: 0 }}>🕹️ {t('systems')}</h1>
          <span style={{ fontSize: '14px', color: colors.textSecondary }}>
            {filteredPOIs} of {totalPOIs} Assets
            {searchTerm.trim() && ` matching "${searchTerm}"`}
          </span>
        </div>
        <div className="header-actions">
          {onOpenDiagrams && (
            <button
              className="btn btn-secondary"
              onClick={onOpenDiagrams}
              title="Open the site relationship diagram (P&ID canvas)"
            >
              🔗 Diagram
            </button>
          )}
          {canEditContent && (
            <button
              className="btn btn-secondary"
              onClick={handleOpenSetSystemsModal}
            >
              Set Systems
            </button>
          )}
          <input
            type="text"
            placeholder={t('searchPOIs')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* THE SYSTEM TREE (migration 058) — the only place a person can shape it by hand.
          Kept apart from the list below on purpose: that one shows systems DERIVED from POI
          property strings, this reads the systems table itself, and merging them would hide
          which one you are looking at. */}
      {resolvedAssetId && (
        <SystemTreePanel
          focusedSystemId={focusedSystemId}
          onFocusHandled={onSystemFocusHandled}
          canEdit={canEditContent}
          onOpenTickets={onOpenTickets}
          onCreateTicket={onCreateTicket}
          assetId={resolvedAssetId}
          systemProperty={selectedSystemProperty}
          subcategoryLevels={subcategoryLevels.filter((level) => level !== '')}
          onApplyDerivation={handleReassignSystems}
        />
      )}


      <div className="locations-content">
        {totalPOIs === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: colors.textSecondary
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              No assets found
            </p>
            <p style={{ fontSize: '14px' }}>
              {t('noSystemAssetsHint', 'Assets are added when you import them from a model in the Viewer.')}
            </p>
          </div>
        ) : filteredPOIs === 0 && searchTerm.trim() ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: colors.textSecondary
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              No assets match your search
            </p>
            <p style={{ fontSize: '14px' }}>
              Try different keywords or clear the search filter
            </p>
          </div>
        ) : (
          <div>
            {categoryGroups.map(categoryGroup => renderCategory(categoryGroup))}
          </div>
        )}
      </div>

      {selectedPOI && (
        <div style={{
          padding: '20px',
          borderTop: `1px solid ${colors.border}`,
          background: colors.backgroundSecondary
        }}>
          <h3 style={{ margin: '0 0 12px 0', color: colors.textPrimary }}>
            Asset Details
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '8px',
            fontSize: '14px'
          }}>
            <strong>ID:</strong> <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedPOI.id}</span>
            <strong>Name:</strong> <span>{selectedPOI.name}</span>
            <strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedPOI.category}</span>
            <strong>Type:</strong> <span>{selectedPOI.properties.type}</span>
            {selectedPOI.properties.description && (
              <>
                <strong>Description:</strong> <span>{selectedPOI.properties.description}</span>
              </>
            )}
            {selectedPOI.properties.system && (
              <>
                <strong>System:</strong> <span>{selectedPOI.properties.system}</span>
              </>
            )}
            {selectedPOI.properties.location && (
              <>
                <strong>Location:</strong> <span>{selectedPOI.properties.location}</span>
              </>
            )}
            {selectedPOI.properties.status && (
              <>
                <strong>Status:</strong> <span>{selectedPOI.properties.status}</span>
              </>
            )}
            <strong>Coordinates:</strong>
            <span style={{ fontFamily: 'monospace' }}>
              X: {selectedPOI.coordinates.x.toFixed(2)},
              Y: {selectedPOI.coordinates.y.toFixed(2)},
              Z: {selectedPOI.coordinates.z?.toFixed(2) || 'N/A'}
            </span>
            <strong>Visibility:</strong> <span>{selectedPOI.visualization.visibility ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
      )}

      {/* Set Systems Modal */}
      {showSetSystemsModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '550px' }}>
            <div className="modal-content">
              <h3>Set Systems</h3>
              <p style={{ color: colors.textSecondary, marginBottom: '16px', fontSize: '14px' }}>
                Configure which properties to use for categorizing assets into the system hierarchy.
                This will update ALL assets based on their property values.
              </p>

              {isLoadingSystemProperties ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: colors.textSecondary
                }}>
                  Loading available properties...
                </div>
              ) : (
                <>
                  {/* System Property - Top Level (Required) */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 600,
                      color: colors.primary,
                      fontSize: '14px'
                    }}>
                      System Property (required):
                    </label>
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={systemPropertySearch}
                      onChange={(e) => setSystemPropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px 4px 0 0',
                        fontSize: '13px',
                        backgroundColor: colors.backgroundSecondary,
                        color: colors.textPrimary,
                        boxSizing: 'border-box'
                      }}
                    />
                    <div
                      style={{
                        width: '100%',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        border: `1px solid ${colors.border}`,
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        backgroundColor: colors.backgroundPrimary
                      }}
                    >
                      <div
                        onClick={() => setSelectedSystemProperty('')}
                        style={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedSystemProperty === '' ? colors.primary + '20' : 'transparent',
                          color: colors.textSecondary,
                          fontSize: '13px',
                          fontStyle: 'italic'
                        }}
                      >
                        -- Select System Property --
                      </div>
                      {availableSystemProperties
                        .filter(prop => prop.toLowerCase().includes(systemPropertySearch.toLowerCase()))
                        .map(prop => (
                          <div
                            key={prop}
                            onClick={() => setSelectedSystemProperty(prop)}
                            style={{
                              padding: '6px 12px',
                              cursor: 'pointer',
                              backgroundColor: selectedSystemProperty === prop ? colors.primary + '20' : 'transparent',
                              color: colors.textPrimary,
                              fontSize: '13px'
                            }}
                          >
                            {prop} ({propertyValues[prop]?.length || 0} values)
                          </div>
                        ))}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                      Top-level grouping - the main system identifier (e.g., "SystemType", "SystemName")
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{
                    borderBottom: `1px solid ${colors.border}`,
                    margin: '16px 0',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: colors.backgroundPrimary,
                      padding: '0 8px',
                      fontSize: '12px',
                      color: colors.textSecondary
                    }}>
                      Subcategories
                    </span>
                  </div>

                  {/* Dynamic Subcategory Levels */}
                  {subcategoryLevels.map((level, index) => (
                    <div key={index} style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{
                          fontWeight: 500,
                          color: colors.textPrimary,
                          fontSize: '13px'
                        }}>
                          Subcategory Level {index + 1}:
                        </label>
                        {subcategoryLevels.length > 1 && (
                          <button
                            onClick={() => removeSubcategoryLevel(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: colors.error,
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '2px 6px'
                            }}
                            title="Remove this subcategory level"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Search properties..."
                        value={subcategorySearches[index] || ''}
                        onChange={(e) => updateSubcategorySearch(index, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px 4px 0 0',
                          fontSize: '13px',
                          backgroundColor: colors.backgroundSecondary,
                          color: colors.textPrimary,
                          boxSizing: 'border-box'
                        }}
                      />
                      <div
                        style={{
                          width: '100%',
                          maxHeight: '120px',
                          overflowY: 'auto',
                          border: `1px solid ${colors.border}`,
                          borderTop: 'none',
                          borderRadius: '0 0 4px 4px',
                          backgroundColor: colors.backgroundPrimary
                        }}
                      >
                        <div
                          onClick={() => updateSubcategoryLevel(index, '')}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            backgroundColor: level === '' ? colors.primary + '20' : 'transparent',
                            color: colors.textSecondary,
                            fontSize: '13px',
                            fontStyle: 'italic'
                          }}
                        >
                          -- None --
                        </div>
                        {availableSystemProperties
                          .filter(prop => prop.toLowerCase().includes((subcategorySearches[index] || '').toLowerCase()))
                          .map(prop => (
                            <div
                              key={prop}
                              onClick={() => updateSubcategoryLevel(index, prop)}
                              style={{
                                padding: '6px 12px',
                                cursor: 'pointer',
                                backgroundColor: level === prop ? colors.primary + '20' : 'transparent',
                                color: colors.textPrimary,
                                fontSize: '13px'
                              }}
                            >
                              {prop} ({propertyValues[prop]?.length || 0} values)
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}

                  {/* Add Subcategory Level Button */}
                  <button
                    onClick={addSubcategoryLevel}
                    className="btn btn-secondary"
                    style={{
                      width: '100%',
                      marginBottom: '16px',
                      fontSize: '13px'
                    }}
                  >
                    + Add Subcategory Level
                  </button>

                  {availableSystemProperties.length === 0 && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: colors.warning + '20',
                      border: `1px solid ${colors.warning}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ color: colors.warning, fontSize: '14px' }}>
                        No properties found in asset data.
                      </div>
                    </div>
                  )}

                  {reassignSystemsResult && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: reassignSystemsResult.updated > 0 ? colors.success + '20' : colors.backgroundSecondary,
                      border: `1px solid ${reassignSystemsResult.updated > 0 ? colors.success : colors.border}`,
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: colors.textPrimary }}>
                        System Assignment Complete
                      </div>
                      <div style={{ fontSize: '14px', color: colors.textSecondary }}>
                        <div>Updated: <strong>{reassignSystemsResult.updated}</strong></div>
                        <div>Unchanged: <strong>{reassignSystemsResult.unchanged}</strong></div>
                        {reassignSystemsResult.failed > 0 && (
                          <div style={{ color: colors.error }}>Failed: <strong>{reassignSystemsResult.failed}</strong></div>
                        )}
                        <div style={{ marginTop: '4px' }}>Total assets: {reassignSystemsResult.total}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSetSystemsModal(false);
                    setReassignSystemsResult(null);
                  }}
                >
                  {reassignSystemsResult ? 'Close' : 'Cancel'}
                </button>
                {!reassignSystemsResult && (
                  <button
                    className="btn btn-primary"
                    onClick={handleReassignSystems}
                    disabled={isReassigningSystems || !selectedSystemProperty}
                  >
                    {isReassigningSystems ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemsPage;